/**
 * Settings-style channel manager.
 *
 * The top-bar Bindings button opens this manager on Email. The left rail then
 * changes context without opening another dialog; the right pane owns the
 * channel-specific content and is the only scrolling region.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Archive,
    CircleOff,
    Pencil,
    Hash,
    Globe,
    LayoutTemplate,
    MessageCircle,
    MessageSquare,
    MessageSquareDashed,
    MessagesSquare,
    PanelTop,
    Pause,
    Play,
    Square,
    X,
} from 'lucide-react';
import { Button } from '@cloudsam/ui';
import { CHANNELS } from '../utils/channelCatalogue';
import EmailBindingPanel from './EmailBindingPanel';
import TemplatesPanel from './TemplatesPanel';
import ChannelBindingPanel from './bindings/ChannelBindingPanel';
import { CHANNEL_BINDING_DEFINITIONS } from './bindings/channelBindingDefinitions';
import '../styles/bindings-manager.css';

const CHANNEL_ICONS = {
    browser: Globe,
    whatsapp: MessageCircle,
    messenger: MessagesSquare,
    sms: MessageSquare,
    rcs: MessageSquareDashed,
    googlechat: MessagesSquare,
    msteams: PanelTop,
    slack: Hash,
};

function NavItem({ id, label, icon: Icon, active, comingSoon, onSelect }) {
    return (
        <button
            type="button"
            className={`bindings-manager__nav-item${active ? ' is-active' : ''}`}
            onClick={() => onSelect(id)}
            aria-current={active ? 'page' : undefined}
        >
            <Icon size={16} strokeWidth={1.5} aria-hidden="true" />
            <span>{label}</span>
            {comingSoon && <span className="bindings-manager__nav-note">Soon</span>}
        </button>
    );
}

const EMPTY_CONTROLS = {
    hasTarget: false,
    state: '',
    busy: false,
    editing: false,
    playLabel: 'Play',
    canPlay: false,
    canPause: false,
    canStop: false,
    canDisable: false,
    canEdit: false,
    readinessPassed: false,
};

function BindingControlStrip({ controls, onAction, canAdmin, allowDisable = true }) {
    const items = [
        { id: 'play', label: controls.playLabel || 'Play', icon: Play, enabled: controls.canPlay },
        { id: 'stop', label: 'Stop', icon: Square, enabled: controls.canStop },
        { id: 'pause', label: 'Pause', icon: Pause, enabled: controls.canPause },
        ...(allowDisable ? [{ id: 'disable', label: 'Disable', icon: CircleOff, enabled: controls.canDisable }] : []),
        { id: 'edit', label: 'Edit', icon: Pencil, enabled: controls.canEdit },
    ];
    return (
        <div className="bindings-manager__control-strip" role="toolbar" aria-label="Selected channel binding controls">
            {items.map(({ id, label, icon: Icon, enabled }) => {
                const adminOnly = id === 'play' || id === 'edit';
                const disabledTitle = adminOnly && !canAdmin
                    ? `${label} requires an administrator.`
                    : controls.hasTarget
                    ? `${label} is unavailable while this binding is ${controls.state || 'not ready'}.`
                    : id === 'edit' && enabled
                        ? label
                        : `Select or add a binding before using ${label}.`;
                return (
                <button
                    key={id}
                    type="button"
                    className={`bindings-manager__control${id === 'edit' && controls.editing ? ' is-active' : ''}`}
                    disabled={!enabled || controls.busy}
                    aria-label={label}
                    aria-pressed={id === 'edit' ? Boolean(controls.editing) : undefined}
                    title={enabled ? label : disabledTitle}
                    onClick={() => onAction(id)}
                >
                    <Icon size={18} strokeWidth={1.5} aria-hidden="true" />
                </button>
                );
            })}
        </div>
    );
}

export default function BindingsOverlay({
    channel,
    canAdmin,
    onClose,
    onSelectChannel,
    organizationLabel,
    email,
}) {
    const dialogRef = useRef(null);
    const panelControlRef = useRef(null);
    const closeHandlerRef = useRef(onClose);
    const [editing, setEditing] = useState(false);
    const [managedControls, setManagedControls] = useState(EMPTY_CONTROLS);
    const activeId = channel?.id || 'browser';
    const templatesSelected = activeId === 'templates';
    const activeChannel = templatesSelected
        ? null
        : CHANNELS.find((item) => item.id === activeId) || CHANNELS[0];
    const ActiveIcon = templatesSelected
        ? LayoutTemplate
        : CHANNEL_ICONS[activeChannel.id] || Archive;
    const canManageSelected = activeId === 'browser' ? Boolean(email?.canManage) : canAdmin;

    useEffect(() => {
        setEditing(false);
        setManagedControls(EMPTY_CONTROLS);
    }, [activeId]);

    const updateManagedControls = useCallback((next) => setManagedControls(next || EMPTY_CONTROLS), []);
    const emailControls = useMemo(() => {
        if (activeId !== 'browser') return EMPTY_CONTROLS;
        const state = email?.state?.binding_state || '';
        const hasTarget = Boolean(email?.state?.ec_binding_id);
        const busy = Boolean(email?.loading || email?.saving || email?.testing);
        const locked = Boolean(email?.state?.editing_locked);
        return {
            hasTarget,
            state,
            busy,
            editing,
            playLabel: state === 'paused' ? 'Resume' : 'Play',
            canPlay: !busy && canManageSelected && Boolean(email?.canTest),
            canPause: hasTarget && !busy && state === 'active',
            canStop: hasTarget && !busy && ['active', 'paused'].includes(state),
            canDisable: false,
            canEdit: !busy && canManageSelected && !locked,
            readinessPassed: false,
        };
    }, [activeId, canManageSelected, editing, email]);
    const controls = templatesSelected ? EMPTY_CONTROLS : activeId === 'browser' ? emailControls : managedControls;
	const flushManagedDraft = async () => {
		if (activeId === 'browser' || templatesSelected) return true;
		try {
			await panelControlRef.current?.flushDraft?.();
			return true;
		} catch (_) {
			return false;
		}
	};
	const requestClose = async () => {
		if (await flushManagedDraft()) onClose?.();
	};

    const runControl = async (action) => {
        if (activeId !== 'browser') {
            panelControlRef.current?.[action]?.();
            return;
        }
        if (action === 'edit') {
            setEditing(true);
            window.setTimeout(() => dialogRef.current?.querySelector('.bindings-manager__scroll input, .bindings-manager__scroll select, .bindings-manager__scroll textarea')?.focus(), 0);
            return;
        }
        if (action === 'play') {
            if (emailControls.state === 'paused') await email?.onTransitionBinding?.('active');
            else await email?.onTestSending?.();
            return;
        }
        if (action === 'pause') {
            await email?.onTransitionBinding?.('paused');
            return;
        }
        if (action === 'stop') {
            await email?.onStopBinding?.();
            return;
        }
    };

	useEffect(() => { closeHandlerRef.current = requestClose; });

    useEffect(() => {
        const invokingControl = document.activeElement;
        const onKey = (event) => {
			if (event.key === 'Escape') closeHandlerRef.current?.();
        };
        document.addEventListener('keydown', onKey);
        dialogRef.current?.querySelector('.bindings-manager__close')?.focus();
        return () => {
            document.removeEventListener('keydown', onKey);
            invokingControl?.focus?.();
        };
    }, []);

    if (!channel) return null;

	const select = async (id) => {
		if (!(await flushManagedDraft())) return;
        if (id === 'templates') {
            onSelectChannel?.({ id: 'templates', name: 'Templates' });
            return;
        }
        const selected = CHANNELS.find((item) => item.id === id);
        if (selected) onSelectChannel?.(selected);
    };

    return (
		<div className="bindings-manager__scrim" onMouseDown={requestClose}>
            <div
                ref={dialogRef}
                className="bindings-manager"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bindings-manager-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <aside className="bindings-manager__rail" aria-label="Channel settings">
                    <header className="bindings-manager__rail-head">
                        <h2>Channels</h2>
                        <p>{organizationLabel || 'Organization settings'}</p>
                        <BindingControlStrip
                            controls={controls}
                            onAction={runControl}
                            canAdmin={canManageSelected}
                            allowDisable={activeId !== 'browser'}
                        />
                    </header>

                    <nav className="bindings-manager__nav">
                        {CHANNELS.map((item) => (
                            <NavItem
                                key={item.id}
                                id={item.id}
                                label={item.name}
                                icon={CHANNEL_ICONS[item.id] || Archive}
                                active={activeId === item.id}
                                comingSoon={item.comingSoon}
                                onSelect={select}
                            />
                        ))}
                    </nav>

                    <div className="bindings-manager__rail-bottom">
                        <NavItem
                            id="templates"
                            label="Templates"
                            icon={LayoutTemplate}
                            active={templatesSelected}
                            onSelect={select}
                        />
                    </div>
                </aside>

                <section className="bindings-manager__content">
                    <header className="bindings-manager__content-head">
                        <div className="bindings-manager__heading">
                            <ActiveIcon size={24} strokeWidth={1.5} aria-hidden="true" />
                            <div>
                                <h2 id="bindings-manager-title">
                                    {templatesSelected ? 'Templates' : `${activeChannel.name} settings`}
                                </h2>
                                <p>
                                    {templatesSelected
                                        ? 'Manage the messages sent with survey invitations.'
										: activeChannel.id === 'browser'
											? 'Survey invitations'
										: CHANNEL_BINDING_DEFINITIONS[activeChannel.id]?.summary || activeChannel.capability}
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="secondary"
                            iconOnly
                            className="bindings-manager__close"
                            aria-label="Close channel settings"
                            title="Close channel settings"
						onClick={requestClose}
                        >
                            <X size={18} strokeWidth={1.5} />
                        </Button>
                    </header>

                    <div className="bindings-manager__scroll">
                        {templatesSelected ? (
                            <TemplatesPanel
                                state={email?.state}
                                organizationId={email?.organizationId}
                                canPersist={Boolean(email?.canTest)}
                                onNotice={email?.onNotice}
                            />
                        ) : activeChannel.id === 'browser' ? (
                            <EmailBindingPanel canAdmin={canManageSelected} editing={editing} onEditingChange={setEditing} {...email} />
                        ) : (
                            <ChannelBindingPanel
								key={activeChannel.id}
                                ref={panelControlRef}
                                channelId={activeChannel.id}
                                canAdmin={canAdmin}
                                editing={editing}
                                onEditingChange={setEditing}
                                onControlStateChange={updateManagedControls}
								onCancel={requestClose}
                            />
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
